import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import styles from './MainPage.module.css'
import TopHeader from '../Componets/TopHeader/TopHeader'
import TherapistList from '../Componets/TherapistList/TherapistList'
import TherapistHeader from '../Componets/TherapistHeader/TherapistHeader'
import MenuSidebar from '../Componets/MenuSidebar/MenuSidebar'
import AddEditTherapist from '../Componets/AddEditTherapist/AddEditTherapist'
import Services from '../Componets/Services/Services'
import WorkHours from '../Componets/WorkHours/WorkHours'
import Attendance from '../Componets/Attendance/Attendance'
import Break from '../Componets/Break/Break'
import DateHeader from '../Componets/DateHeader/DateHeader'
import Loader from '../../../Components/Loader/Loader'
import ArchiveTherapistPopup from '../Componets/ArchiveTherapistPopup/ArchiveTherapistPopup'
import StaffIcon from '../../../Components/Icons/StaffIcon'
import FilterIcon from '../../../Components/Icons/FilterIcon'
import CloseCircleIcon from '../../../Components/Icons/CloseCircleIcon'
import { getSelectedOutlet, getTherapistDetails, updateTherapistStatus, getTherapistTimings } from '../../../APICalling/APICalling'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useGlobalContext } from '../../../Context/GlobalContext';
import { formatDateForBackend, extractOutletFromApiResponse } from '../../../Utils/utils';

const Therapists = () => {
	const { selectedOutlet, selectedDate, setGlobalTherapistList, homeStaffList, setHomeStaffList, setSelectedOutletData } = useGlobalContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedTherapist, setSelectedTherapist] = useState(null);
	const [activeTab, setActiveTab] = useState('details');
	const [mode, setMode] = useState('show'); // 'show', 'edit', 'create'
	const [isCreatingNew, setIsCreatingNew] = useState(false);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [startDate, setStartDate] = useState(getStartOfWeek(new Date()));
	const [showArchivePopup, setShowArchivePopup] = useState(false);
	const [archivePopupType, setArchivePopupType] = useState('archive'); // 'archive', 'archive-with-bookings', 'unarchive'
	const [isProcessingArchive, setIsProcessingArchive] = useState(false);
	// Mobile menu states
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isTherapistListOpen, setIsTherapistListOpen] = useState(false);

	// Get start of current week (Monday)
	function getStartOfWeek(date) {
		const d = new Date(date);
		const day = d.getDay();
		const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
		return new Date(d.setDate(diff));
	}

	const handleDateChange = (newDate) => {
		setStartDate(newDate);
	};

	// Handle URL parameters on component mount
	useEffect(() => {
		const therapistId = searchParams.get('therapist');
		const tab = searchParams.get('tab');
		
		if (therapistId && !selectedTherapist) {
			// Load therapist from URL parameter
			handleTherapistSelect({ id: therapistId });
		}
		
		if (tab && tab !== activeTab) {
			setActiveTab(tab);
		}
	}, [searchParams]); // Only run when search params change

	const selectedOutletRequestIdRef = useRef(0);
	useEffect(() => {
		if (!selectedOutlet) return;

		const reqId = ++selectedOutletRequestIdRef.current;
		const outletIdForRequest = selectedOutlet;

		const fetchSelectedOutletData = async () => {
			try {
				const response = await getSelectedOutlet(outletIdForRequest, { is_image: 1 });
				if (reqId !== selectedOutletRequestIdRef.current) return;
				const data = extractOutletFromApiResponse(response);
				if (data && Number(data.id) === Number(outletIdForRequest)) {
					setSelectedOutletData(data);
				}
			} catch (error) {
				if (reqId !== selectedOutletRequestIdRef.current) return;
				console.error('Error fetching selected outlet details:', error);
			}
		};

		fetchSelectedOutletData();
	}, [selectedOutlet, setSelectedOutletData]);

	const handleTherapistSelect = async (therapist) => {
		setIsLoadingDetails(true);
		try {
			// Call API to get full therapist details
			const response = await getTherapistDetails(therapist.id);
			
			if (response?.data?.success) {
				setSelectedTherapist(response.data.data);
				// Keep the current active tab (don't reset to details)
				// setActiveTab('details');
				setMode('show');
				setIsCreatingNew(false);
				// Close mobile therapist list after selection
				setIsTherapistListOpen(false);
			} else {
				toast.error('Failed to load therapist details');
				setSelectedTherapist(null);
			}
		} catch (error) {
			console.error('Error fetching therapist details:', error);
			toast.error('An error occurred while loading therapist details');
			setSelectedTherapist(null);
		} finally {
			setIsLoadingDetails(false);
		}
	};

	const handleAddStaff = () => {
		setIsCreatingNew(true);
		setSelectedTherapist(null);
		setMode('create');
		setActiveTab('details');
	};

	const handleOutletChange = () => {
		// Clear selected therapist when outlet changes
		setSelectedTherapist(null);
		setMode('show');
		setIsCreatingNew(false);
		setActiveTab('details');
	};

	const handleTabChange = (tabId) => {
		setActiveTab(tabId);
		// Close mobile menu after tab selection
		setIsMobileMenuOpen(false);
	};

	const therapistHasHomeSchedule = async (therapistId) => {
		if (!therapistId || !selectedOutlet || !selectedDate) return false;

		try {
			const dateStr = formatDateForBackend(selectedDate);
			const timingsResponse = await getTherapistTimings({user: therapistId, outlet: selectedOutlet, start_date: dateStr, end_date: dateStr, leave: 0});
			const timingsData = timingsResponse?.data?.list || timingsResponse?.data?.data?.list || {};
			const therapistTimings = timingsData?.[String(therapistId)];
			const dayShifts = therapistTimings?.[dateStr];
			const shift = Array.isArray(dayShifts) ? dayShifts[0] : null;

			return Boolean(
				shift && (
					shift.checkin || shift.checkout || shift.schedule_start_time || shift.schedule_end_time || shift.timing_code_id != null)
			);
		} catch (error) {
			console.error('Error checking therapist schedule:', error);
			return false;
		}
	};

	const handleSave = async (updatedData) => {
		//console.log('Therapist data saved:', updatedData);
		
		// Refresh the therapist list
		setRefreshTrigger(prev => prev + 1);

		// Keep Home calendar therapist headers in sync without full page refresh.
		if (typeof setHomeStaffList === 'function' && updatedData?.id) {
			const normalizedStatus = Number(updatedData?.status ?? selectedTherapist?.status ?? 1);
			const normalizedTherapist = {
				...updatedData,
				status: normalizedStatus,
				alias: updatedData?.alias || updatedData?.name || '',
				name: updatedData?.name || updatedData?.alias || '',
				pager_number: updatedData?.pager_number ?? selectedTherapist?.pager_number ?? '',
			};
			const isActive = normalizedStatus === 1;
			const canShowInHomeStaffList = isActive ? await therapistHasHomeSchedule(normalizedTherapist.id) : false;

			setHomeStaffList((prev) => {
				if (!Array.isArray(prev)) return prev;

				const therapistId = String(normalizedTherapist.id);
				const therapistIndex = prev.findIndex((staff) => String(staff?.id) === therapistId);

				// Home only shows active therapists that have a schedule for the selected date.
				if (!canShowInHomeStaffList) {
					return prev.filter((staff) => String(staff?.id) !== therapistId);
				}

				// If scheduled therapist is not present yet, add it.
				if (therapistIndex === -1) {
					return [...prev, normalizedTherapist];
				}

				// If already present, update key fields (name/alias/pager/status) and preserve the rest.
				const next = [...prev];
				next[therapistIndex] = {
					...prev[therapistIndex],
					...normalizedTherapist,
				};
				return next;
			});
		}
		
		if (isCreatingNew) {
			// After creating new therapist, show their details
			setSelectedTherapist(updatedData);
			setIsCreatingNew(false);
			setMode('show');
		} else {
			// Update the selected therapist with the new data
			setSelectedTherapist(updatedData);
			setMode('show');
		}
	};

	const handleCancelCreate = () => {
		setIsCreatingNew(false);
		setSelectedTherapist(null);
		setMode('show');
	};


	const handleArchive = () => {
		if (!selectedTherapist?.id) {
			toast.error('No therapist selected');
			return;
		}

		// Check hasActiveBookings from backend response (therapist details API)
		if (selectedTherapist?.has_active_booking) {
			// Show popup with message that they can't archive due to bookings
			setArchivePopupType('archive-with-bookings');
		} else {
			// Show normal archive confirmation
			setArchivePopupType('archive');
		}
		setShowArchivePopup(true);
	};

	const handleUnarchive = () => {
		if (!selectedTherapist?.id) {
			toast.error('No therapist selected');
			return;
		}

		// Show unarchive confirmation
		setArchivePopupType('unarchive');
		setShowArchivePopup(true);
	};

	const handleConfirmArchiveUnarchive = async () => {
		if (!selectedTherapist?.id) {
			toast.error('No therapist selected');
			return;
		}

		const therapistId = selectedTherapist.id;
		const therapistSnapshot = selectedTherapist;

		setIsProcessingArchive(true);

		try {
			const newStatus = archivePopupType === 'unarchive' ? 1 : 0;
			const dataToPost = {
				status: newStatus,
				therapist_id: therapistId,
				outlet: selectedOutlet
			};

			const response = await updateTherapistStatus(therapistId, dataToPost);

			if (response?.data?.success || response?.success) {
				const successMessage = archivePopupType === 'unarchive' 
					? 'Therapist unarchived successfully' 
					: 'Therapist archived successfully';
				toast.success(successMessage);

				// Ensure Details tab is shown after archive/unarchive
				setActiveTab('details');
				setMode('show');
				setIsCreatingNew(false);

				// Refresh the therapist list
				setRefreshTrigger(prev => prev + 1);

				// Keep therapist selected and refresh their details (so list selection + header stay in sync)
				let updatedTherapist = { ...therapistSnapshot, status: newStatus };
				try {
					const detailsResponse = await getTherapistDetails(therapistId);
					if (detailsResponse?.data?.success) {
						updatedTherapist = detailsResponse.data.data;
						setSelectedTherapist(updatedTherapist);
					} else {
						setSelectedTherapist(updatedTherapist);
					}
				} catch (e) {
					setSelectedTherapist(updatedTherapist);
				}

				// Close popup
				setShowArchivePopup(false);

				// Clear reports therapist cache so report filters refresh
				if (setGlobalTherapistList) {
					setGlobalTherapistList({ outletId: null, data: [], archivedData: [] });
				}

				// Sync homeStaffList on archive/unarchive
				let canAddToHomeStaffList = true;
				if (newStatus === 1) {
					canAddToHomeStaffList = false;
					try {
						if (selectedOutlet && selectedDate) {
							const dateStr = formatDateForBackend(selectedDate);
							const timingsParams = {
								user: therapistId,
								outlet: selectedOutlet,
								start_date: dateStr,
								end_date: dateStr,
								leave: 0,
							};
							const timingsResponse = await getTherapistTimings(timingsParams);
							const timingsData = timingsResponse?.data?.list
								|| timingsResponse?.data?.data?.list
								|| {};
							const therapistTimings = timingsData?.[String(therapistId)];
							const dayShifts = therapistTimings?.[dateStr];
							const shift = Array.isArray(dayShifts) ? dayShifts[0] : null;
							canAddToHomeStaffList = Boolean(
								shift && (shift.checkin || shift.checkout || shift.timing_code_id != null)
							);
						}
					} catch (e) {
						canAddToHomeStaffList = false;
					}
				}

				if (typeof setHomeStaffList === 'function') {
					setHomeStaffList((prev) => {
						if (!Array.isArray(prev)) return prev;

						if (newStatus === 0) {
							return prev.filter((staff) => String(staff.id) !== String(therapistId));
						}

						if (!canAddToHomeStaffList) return prev;

						const exists = prev.some((staff) => String(staff.id) === String(therapistId));
						if (exists) return prev;

						return [...prev, updatedTherapist];
					});
				}
			} else {
				const errorMessage = response?.data?.message || response?.message || 'Failed to update therapist status';
				toast.error(errorMessage);
			}
		} catch (error) {
			console.error('Error updating therapist status:', error);
			toast.error('An error occurred while updating therapist status');
		} finally {
			setIsProcessingArchive(false);
		}
	};

	const handleCancelArchivePopup = () => {
		setShowArchivePopup(false);
		setIsProcessingArchive(false);
	};

	const handleAttendanceUpdate = async () => {
		// Refresh the therapist list to update status indicators
		setRefreshTrigger(prev => prev + 1);
		
		// Refresh the selected therapist details to update header status indicator
		if (selectedTherapist?.id) {
			try {
				const response = await getTherapistDetails(selectedTherapist.id);
				if (response?.data?.success) {
					setSelectedTherapist(response.data.data);
				}
			} catch (error) {
				console.error('Error refreshing therapist details:', error);
			}
		}
	};


	return (
		<>
			<ToastContainer position="top-right" autoClose={3000} />
			
			{/* Archive/Unarchive Confirmation Popup */}
			{showArchivePopup && (
				<ArchiveTherapistPopup
					type={archivePopupType}
					onConfirm={handleConfirmArchiveUnarchive}
					onCancel={handleCancelArchivePopup}
					isProcessing={isProcessingArchive}
				/>
			)}

		<div className={`${styles.bookingsContainer} header-sapce`}>
			<TopHeader onAddStaffClick={handleAddStaff} onOutletChange={handleOutletChange} />
			
			{/* Mobile Navigation Buttons */}
			{!isCreatingNew && (
				<div className={styles.mobileNavButtons}>
					{/* Therapist List Button - Always show on mobile (except when creating) */}
					<button 
						className={styles.mobileTherapistListToggle}
						onClick={() => setIsTherapistListOpen(!isTherapistListOpen)}
						aria-label="Toggle therapist list"
						title="Therapists"
					>
						<StaffIcon size={20} color="currentColor" />
						<span>Therapists</span>
					</button>
					
					{/* Menu Button - Only show when therapist is selected */}
					{selectedTherapist && (
						<button 
							className={styles.mobileMenuToggleFixed}
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
							aria-label="Toggle menu"
							title="Menu"
						>
							<FilterIcon size={20} fill="currentColor" />
							<span>Menu</span>
						</button>
					)}
				</div>
			)}

			<div className={styles.mainContent}>
					{/* Left Panel - Therapist List */}
					<div className={`${styles.leftPanel} ${isTherapistListOpen ? styles.mobileOpen : ''}`}>
						{/* Mobile Close Button */}
						<button 
							className={styles.mobileCloseButton}
							onClick={() => setIsTherapistListOpen(false)}
							aria-label="Close therapist list"
						>
							<CloseCircleIcon size={28} color="white" crossColor="var(--neutral-600)" />
						</button>
						<TherapistList 
							onSelectTherapist={handleTherapistSelect}
							refreshTrigger={refreshTrigger}
							activeTherapistId={selectedTherapist?.id}
							forceShowArchived={selectedTherapist?.status === 0}
							onClearSelection={() => {
								setSelectedTherapist(null);
								setMode('show');
								setIsCreatingNew(false);
								setActiveTab('details');
								setIsMobileMenuOpen(false);
							}}
						/>
					</div>

					{/* Overlay for mobile therapist list */}
					{isTherapistListOpen && (
						<div 
							className={styles.mobileOverlay}
							onClick={() => setIsTherapistListOpen(false)}
						/>
					)}

					{/* Right Panel - Main Content */}
					<div className={styles.rightPanel}>
						{isLoadingDetails ? (
							/* Loading therapist details */
							<div className={styles.loaderContainer}>
								<Loader />
							</div>
						) : isCreatingNew ? (
							/* Creating New Therapist - Show only form without menu and header */
							<div className={styles.createTherapistContainer}>
								<AddEditTherapist 
									therapist={null}
									mode="create"
									onSave={handleSave}
									onCancel={handleCancelCreate}
								/>
							</div>
						) : !selectedTherapist ? (
							/* No therapist selected - Show welcome message */
							<div className={styles.contentHeader}>
								<div>
									<h1 className={styles.title}>Manage your therapists</h1>
									<p className={styles.description}>
										You can create new therapists or change the details, services and work hours of existing therapists.
									</p>
								</div>
							</div>
						) : (
							/* Therapist selected - Show full layout with header and menu */
							<div className={styles.therapistDetailContainer}>
								{/* Therapist Header */}
								<TherapistHeader 
									therapist={selectedTherapist}
									onArchive={handleArchive}
									onUnarchive={handleUnarchive}
								/>

								{/* Content Area with Sidebar and Details */}
								<div className={styles.detailContent}>
									{/* Menu Sidebar */}
									<div className={`${styles.menuSidebarWrapper} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
										{/* Mobile Close Button for Menu */}
										<button 
											className={styles.mobileMenuCloseButton}
											onClick={() => setIsMobileMenuOpen(false)}
											aria-label="Close menu"
										>
											<CloseCircleIcon size={28} color="white" crossColor="var(--neutral-600)" />
										</button>
										<MenuSidebar 
											activeTab={activeTab}
											onTabChange={handleTabChange}
										/>
									</div>

									{/* Overlay for mobile menu */}
									{isMobileMenuOpen && (
										<div 
											className={styles.mobileMenuOverlay}
											onClick={() => setIsMobileMenuOpen(false)}
										/>
									)}

									{/* Tab Content */}
									<div className={styles.tabContent}>
										{activeTab === 'details' && (
											<AddEditTherapist 
												therapist={selectedTherapist}
												mode={mode}
												onSave={handleSave}
												onCancel={() => setMode('show')}
											/>
										)}
									{activeTab === 'services' && (
										<Services therapistId={selectedTherapist?.id} />
									)}
										{/* Date Header for Work Hours, Attendance, and Break */}
										{(activeTab === 'workHours' || activeTab === 'attendance' || activeTab === 'break') && (
											<div className={styles.dateHeaderWrapper}>
												<DateHeader startDate={startDate} onDateChange={handleDateChange} />
											</div>
										)}
					{activeTab === 'workHours' && (
						<WorkHours 
							therapistId={selectedTherapist?.id} 
							startDate={startDate}
						/>
					)}
									{activeTab === 'attendance' && (
										<Attendance 
											therapistId={selectedTherapist?.id} 
											startDate={startDate}
											onAttendanceUpdate={handleAttendanceUpdate}
										/>
									)}
										{activeTab === 'break' && (
											<Break therapistId={selectedTherapist?.id} startDate={startDate} />
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};

export default Therapists;